import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
// import { useNavigate } from "react-router-dom";
import axios from "axios";

import DashboardSidebar from "../../components/dashboard components/DashboardSidebar";
import DashboardHeader from "../../components/dashboard components/DashboardHeader";

import HeroSectionForm from "../../components/dashboard components/forms/HeroSectionForm";
import WhoWeAreForm from "../../components/dashboard components/forms/WhoWeAreForm";
import OurJourneyForm from "../../components/dashboard components/forms/OurJourneyForm";
import DeliveredAreaForm from "../../components/dashboard components/forms/DeliveredAreaForm";

// ✅ NEW IMPORT
import OurPartnersForm from "../../components/dashboard components/forms/OurPartnersForm";

import VisionValuesForm from "../../components/dashboard components/forms/VisionValuesForm";
import OurFootPrintForm from "../../components/dashboard components/forms/OurFootPrintForm";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const About = () => {
  // const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("about");
  const [activeTab, setActiveTab] = useState("hero");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // const [tabMenuOpen, setTabMenuOpen] = useState(false);

  // ===================
  // Tabs config
  // ===================
  const tabs = [
    { key: "hero", label: "Hero Section" },
    { key: "who-we-are", label: "Who We Are" },
    { key: "journey", label: "Our Journey" },
    { key: "delivered-area", label: "Delivered Area" },

    // ✅ ADD PARTNERS TAB HERE
    { key: "partners", label: "Our Partners" },

    { key: "vision-values", label: "Vision & Values" },
    { key: "footprint", label: "Our Footprint" },
  ];

  const activeTabLabel =
    tabs.find((t) => t.key === activeTab)?.label || "Select Section";

  // ===================
  // Data States
  // ===================
  const [heroSlides, setHeroSlides] = useState([]);
  const [whoWeAreData, setWhoWeAreData] = useState({});
  const [journeyData, setJourneyData] = useState({});
  const [deliveredAreaData, setDeliveredAreaData] = useState([]);

  // ✅ NEW PARTNERS STATE
  const [partnersData, setPartnersData] = useState([]);

  const [visionValuesData, setVisionValuesData] = useState({});
  const [footprintData, setFootprintData] = useState({});

  // ===================
  // Fetchers
  // ===================
  const fetchHero = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/hero`);
      const data = res.data;
      setHeroSlides(data && Object.keys(data).length ? [data] : []);
    } catch (err) {
      console.error("Error fetching about hero data:", err);
    }
  };

  const fetchWhoWeAre = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/who-we-are`);
      setWhoWeAreData(res.data || {});
    } catch (err) {
      console.error("Error fetching Who We Are data:", err);
    }
  };

  const fetchJourney = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/journey`);
      setJourneyData(res.data || {});
    } catch (err) {
      console.error("Error fetching Journey data:", err);
    }
  };

  const fetchDeliveredArea = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/delivered-area`);
      setDeliveredAreaData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching Delivered Area data:", err);
    }
  };

  // ✅ NEW: Fetch Partners
  const fetchPartners = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/partners`);
      setPartnersData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching partners:", err);
    }
  };

  const fetchVisionValues = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/vision-values`);
      setVisionValuesData(res.data || {});
    } catch (err) {
      console.error("Error fetching Vision & Values data:", err);
    }
  };

  const fetchFootprint = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/about/footprint`);
      setFootprintData(res.data || {});
    } catch (err) {
      console.error("Error fetching Footprint data:", err);
    }
  };

  useEffect(() => {
    fetchHero();
    fetchWhoWeAre();
    fetchJourney();
    fetchDeliveredArea();

    // ✅ FETCH PARTNERS ON LOAD
    fetchPartners();

    fetchVisionValues();
    fetchFootprint();
  }, []);

  // ===================
  // Save Handlers
  // ===================
  const authConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
    },
  });

  const handleSaveHero = async (data) => {
    try {
      await axios.post(`${API_BASE_URL}/about/hero`, data, authConfig());
      alert("Hero section saved successfully.");
      fetchHero();
    } catch (error) {
      alert("Failed to save hero section.");
    }
  };

  const handleSaveWhoWeAre = async (data) => {
    try {
      await axios.post(`${API_BASE_URL}/about/who-we-are`, data, authConfig());
      alert("Who We Are section saved successfully.");
      fetchWhoWeAre();
    } catch (error) {
      alert("Failed to save Who We Are section.");
    }
  };

  const handleSaveJourney = async (data) => {
    try {
      await axios.post(`${API_BASE_URL}/about/journey`, data, authConfig());
      alert("Journey section saved successfully.");
      fetchJourney();
    } catch (error) {
      alert("Failed to save Journey section.");
    }
  };

  const handleSaveDeliveredArea = async (data) => {
    try {
      await axios.post(
        `${API_BASE_URL}/about/delivered-area`,
        data,
        authConfig()
      );
      alert("Delivered Area saved successfully.");
      fetchDeliveredArea();
    } catch (error) {
      alert("Failed to save Delivered Area.");
    }
  };

  // ✅ NEW: Save Partner
  const handleSavePartners = async (data) => {
    try {
      if (data.partner_id) {
        await axios.put(
          `${API_BASE_URL}/about/partners/${data.partner_id}`,
          data,
          authConfig()
        );
      } else {
        await axios.post(`${API_BASE_URL}/about/partners`, data, authConfig());
      }

      alert("Partner saved successfully.");
      fetchPartners();
    } catch (error) {
      alert("Failed to save partner.");
    }
  };

  const handleSaveVisionValues = async (data) => {
    try {
      await axios.post(
        `${API_BASE_URL}/about/vision-values`,
        data,
        authConfig()
      );
      alert("Vision & Values section saved successfully.");
      fetchVisionValues();
    } catch {
      alert("Failed to save Vision & Values.");
    }
  };

  const handleSaveFootprint = async (data) => {
    try {
      await axios.post(
        `${API_BASE_URL}/about/footprint`,
        data,
        authConfig()
      );
      alert("Footprint section saved successfully.");
      fetchFootprint();
    } catch {
      alert("Failed to save Footprint.");
    }
  };

  // ===================
  // Render Tabs
  // ===================
  const renderActiveTab = () => {
    switch (activeTab) {
      case "hero":
        return (
          <HeroSectionForm
            slides={heroSlides}
            onSave={handleSaveHero}
            onRefetch={fetchHero}
          />
        );

      case "who-we-are":
        return (
          <WhoWeAreForm
            data={whoWeAreData}
            onSave={handleSaveWhoWeAre}
            onRefetch={fetchWhoWeAre}
          />
        );

      case "journey":
        return (
          <OurJourneyForm
            data={journeyData}
            onSave={handleSaveJourney}
            onRefetch={fetchJourney}
          />
        );

      case "delivered-area":
        return (
          <DeliveredAreaForm
            data={deliveredAreaData}
            onSave={handleSaveDeliveredArea}
            onRefetch={fetchDeliveredArea}
          />
        );

      // ✅ NEW: PARTNERS SECTION
      case "partners":
        return (
          <OurPartnersForm
            data={partnersData}
            onSave={handleSavePartners}
            onRefetch={fetchPartners}
          />
        );

      case "vision-values":
        return (
          <VisionValuesForm
            data={visionValuesData}
            onSave={handleSaveVisionValues}
            onRefetch={fetchVisionValues}
          />
        );

      case "footprint":
        return (
          <OurFootPrintForm
            data={footprintData}
            onSave={handleSaveFootprint}
            onRefetch={fetchFootprint}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-50 font-sans">
      {/* Sidebar Desktop */}
      <div className="hidden lg:block">
        <DashboardSidebar
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      <main className="flex-1 flex flex-col w-full">

        {/* Header */}
        <div className="sticky top-0 bg-white z-30 shadow-sm flex items-center justify-between px-4 py-4">
          <button
            className="lg:hidden text-gray-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={28} />
          </button>

          <div className="flex-1 flex justify-center">
            <DashboardHeader title="About Page Management" backTo="/dashboard" />
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b shadow-sm">
          <div className="hidden lg:flex justify-center gap-3 py-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-3 font-semibold border-b-4 ${
                  activeTab === tab.key
                    ? "border-blue-600 text-blue-700 bg-blue-50"
                    : "border-transparent text-gray-600 hover:bg-gray-50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6 max-w-7xl mx-auto w-full bg-white mt-4 rounded-xl shadow-md">
          {renderActiveTab()}
        </div>
      </main>
    </div>
  );
};

export default About;
